import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ChevronDown, ChevronUp, ArrowRight, Sparkles, Star } from "lucide-react";
import { LogoMarkStatic } from "@/components/LogoMark";
import SeoHead from "@/components/SeoHead";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


function FeatureCell({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-emerald-400 mx-auto" />;
  if (value === false) return <X className="h-4 w-4 text-slate-600 mx-auto" />;
  return <span className="text-xs font-mono text-slate-200 tabular-nums">{value}</span>;
}


export default function PricingPage() {
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [faqOpen, setFaqOpen] = useState({});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/pricing`).then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="min-h-screen bg-slate-950 text-slate-400 flex items-center justify-center">Loading pricing...</div>;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="pricing-page">
      <SeoHead route="/pricing" />
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/8 bg-slate-950/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
            <LogoMarkStatic size={32} />
            <span className="font-heading text-xl tracking-tight">Acquigine</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-400 hover:text-slate-100">Sign in</Link>
            <Link to="/signup" className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25" data-testid="pricing-header-cta">
              Start free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-8 text-center">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-3">Pricing</div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">
          Find more deals. Close faster.
        </h1>
        <p className="text-base text-slate-400 max-w-2xl mx-auto">
          Every plan includes the Creative Finance Readiness Score, the 10-stage pipeline, and access to public data sources. Upgrade when you are ready to send outreach and close deals.
        </p>

        {/* Billing toggle */}
        <div className="mt-8 inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-sm p-1" data-testid="pricing-billing-toggle">
          {["monthly", "annual"].map((b) => (
            <button key={b}
                    onClick={() => setBilling(b)}
                    data-testid={`pricing-billing-${b}`}
                    className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${
                      billing === b ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400 hover:text-slate-200"
                    }`}>
              {b === "annual" ? "Annual, 2 months free" : "Monthly"}
            </button>
          ))}
        </div>
      </section>

      {/* Tier cards */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid lg:grid-cols-3 gap-5">
          {data.tiers.map((t) => {
            const isPro = t.id === "pro";
            const price = billing === "annual" ? t.annual_price / 12 : t.monthly_price;
            return (
              <div key={t.id}
                   data-testid={`pricing-tier-${t.id}`}
                   className={`ae-card p-6 flex flex-col ${isPro ? "border-emerald-500/50 shadow-[0_0_40px_-12px_rgba(34,197,94,0.3)]" : ""}`}>
                {t.badge && (
                  <div className="inline-flex items-center gap-1 self-start mb-3 px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[9px] uppercase tracking-wider rounded-sm font-mono">
                    <Star className="h-2.5 w-2.5 fill-current" /> {t.badge}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">{t.name}</div>
                <h3 className="font-heading text-2xl tracking-tight mt-1">{t.tagline}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-5xl tracking-tight tabular-nums">${billing === "annual" && t.id !== "free" ? price.toFixed(2) : price}</span>
                  <span className="text-slate-500 text-sm">/mo</span>
                </div>
                {billing === "annual" && t.id !== "free" && (
                  <div className="text-[11px] text-slate-500 mt-0.5">${t.annual_price} billed annually (2 months free)</div>
                )}

                <Link to={t.cta.url} data-testid={`pricing-cta-${t.id}`}
                      className={`mt-6 px-4 py-2.5 text-xs uppercase tracking-wider rounded-sm border inline-flex items-center justify-center gap-1.5 ${
                        isPro ? "bg-emerald-500 text-slate-950 border-emerald-500 hover:bg-emerald-400"
                              : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25"
                      }`}>
                  {t.cta.label} <ArrowRight className="h-3 w-3" />
                </Link>
                <div className="text-[11px] text-slate-500 mt-2 text-center">{t.cta_subtext}</div>

                <ul className="mt-6 space-y-2.5 text-sm">
                  {t.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-slate-300">
                      <Check className="h-3.5 w-3.5 text-emerald-400 mt-1 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Comparison table */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="font-heading text-3xl tracking-tight mb-6 text-center">Compare every feature</h2>
        <div className="overflow-x-auto" data-testid="pricing-comparison">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8">
                <th className="text-left py-3 text-[10px] uppercase tracking-widest text-slate-500 font-medium">Feature</th>
                <th className="text-center py-3 text-[10px] uppercase tracking-widest text-slate-500 font-medium w-32">Free</th>
                <th className="text-center py-3 text-[10px] uppercase tracking-widest text-emerald-400 font-medium w-32">Pro</th>
                <th className="text-center py-3 text-[10px] uppercase tracking-widest text-slate-500 font-medium w-32">Business</th>
              </tr>
            </thead>
            <tbody>
              {data.comparison_features.map((f, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-3 text-slate-300">{f.label}</td>
                  <td className="text-center"><FeatureCell value={f.values.free} /></td>
                  <td className="text-center bg-emerald-500/5"><FeatureCell value={f.values.pro} /></td>
                  <td className="text-center"><FeatureCell value={f.values.business} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-12">
        <h2 className="font-heading text-3xl tracking-tight mb-6 text-center">Frequently asked questions</h2>
        <div className="space-y-2" data-testid="pricing-faq">
          {data.faq.map((f, i) => (
            <div key={i} className="border border-white/8 rounded-sm bg-white/2" data-testid={`faq-${i}`}>
              <button onClick={() => setFaqOpen({ ...faqOpen, [i]: !faqOpen[i] })}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                <span className="text-sm text-slate-200">{f.q}</span>
                {faqOpen[i] ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
              </button>
              {faqOpen[i] && (
                <div className="px-4 pb-3 text-sm text-slate-400 leading-relaxed border-t border-white/5 pt-3">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Sparkles className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight mb-3">Ready to find your next deal?</h2>
        <p className="text-slate-400 mb-6">Free forever, no credit card. Upgrade when you are ready to send outreach and close.</p>
        <Link to="/signup" data-testid="pricing-final-cta"
              className="inline-flex items-center gap-1.5 px-6 py-3 text-sm uppercase tracking-wider rounded-sm bg-emerald-500 text-slate-950 hover:bg-emerald-400">
          Start free <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <footer className="border-t border-white/8 py-6 text-center text-xs text-slate-600">
        Acquigine Acquisition Intelligence Platform, acquigine.com
      </footer>
    </div>
  );
}
